import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs'

export default class extends Controller {
  static values = { scope: { type: String, default: 'list' } }

  connect() {
    // Nested subtasks-sort controllers reconnect when Sortable moves a row.
    // Don't strip classes from an in-progress drag.
    if (!Sortable.active) {
      this.cleanupSortableClasses();
    }

    // If this is a lazy-loading turbo frame, wait for it to load
    if (this.element.tagName === 'TURBO-FRAME' && this.element.hasAttribute('src')) {
      this.element.addEventListener('turbo:frame-load', () => {
        this.initializeSortable();
      }, { once: true });
    } else {
      this.initializeSortable();
    }
  }

  disconnect() {
    this.stopCollapsedSectionDropTargeting();

    if (!this.sortableInstance) return;

    // Every subtask row contains a nested subtasks-sort turbo-frame. When
    // Sortable moves the row, Stimulus disconnects that nested controller.
    // Sortable.destroy() calls _onDrop on the ACTIVE (parent) drag via global
    // state — which aborted multi-position moves and caused the snap-back.
    if (Sortable.active) {
      this.sortableInstance = null;
      return;
    }

    // A nested controller sharing this container may have already destroyed
    // this instance via Sortable.get(); destroy() nulls `el`, and a second
    // destroy() throws on it.
    if (this.sortableInstance.el) {
      this.cleanupSortableClasses();
      this.sortableInstance.destroy();
    }
    this.sortableInstance = null;
  }

  initializeSortable() {
    if (typeof Sortable === 'undefined') {
      console.error('Sortable library is not loaded');
      return;
    }

    const sortableContainer = this.sortableContainer();
    if (!sortableContainer) {
      console.warn('Could not find sortable container');
      return;
    }

    // After a mid-drag disconnect skipped destroy, reuse the live instance.
    const existing = Sortable.get(sortableContainer);
    if (existing) {
      if (Sortable.active) {
        this.sortableInstance = existing;
        return;
      }
      existing.destroy();
    }

    const options = {
      animation: 150,
      draggable: this.draggableSelector(sortableContainer),
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      handle: '.sortable-item',
      // Never treat section chrome as a drag target
      filter: '.subtask-section-header',
      preventOnFilter: false,
      revertOnSpill: false,

      onStart: (event) => {
        this.collapsedState = this.collapseNestedSubtasks(event.item);
        this.startCollapsedSectionDropTargeting();
      },

      onEnd: (event) => {
        this.stopCollapsedSectionDropTargeting();
        this.cleanupSortableClasses();
        this.syncSectionCounts();

        if (this.collapsedState) {
          this.restoreExpandedState(event.item, this.collapsedState);
          this.collapsedState = null;
        }

        this.updatePosition(event);
      }
    };

    // Shared group name lets rows move between section containers. A larger
    // emptyInsertThreshold keeps empty (zero-height) sections droppable.
    if (this.scopeValue === 'section') {
      options.group = 'subtasks';
      options.emptyInsertThreshold = 48;
    }

    this.sortableInstance = new Sortable(sortableContainer, options);
  }

  // Section scope: this.element is the section items container.
  // List scope: prefer an inner .border.rounded, else the element itself (turbo-frame / nested).
  sortableContainer() {
    if (this.scopeValue === 'section') {
      return this.element;
    }

    return this.element.querySelector('.border.rounded') || this.element;
  }

  // Prefer an explicit item marker so we never match section-level turbo-collapse wrappers.
  // Section scope always uses the marker — empty sections have no children, and falling
  // back to [data-controller="turbo-collapse"] breaks cross-list puts into those lists.
  draggableSelector(container) {
    if (this.scopeValue === 'section') {
      return '[data-subtasks-sort-item]';
    }

    if (container.querySelector(':scope > [data-subtasks-sort-item]')) {
      return '[data-subtasks-sort-item]';
    }

    if (container.querySelector(':scope > .sortable-item')) {
      return '.sortable-item';
    }

    // Legacy flat-list fallback (pre-marker markup)
    return '[data-controller="turbo-collapse"]';
  }

  cleanupSortableClasses() {
    document.querySelectorAll('.sortable-ghost, .sortable-chosen').forEach(el => {
      el.classList.remove('sortable-ghost', 'sortable-chosen');
    });
  }

  // During a drag: expand collapsed sections under the pointer, and give empty
  // (zero-height) section lists a temporary hit target so Sortable can accept puts.
  startCollapsedSectionDropTargeting() {
    if (this.scopeValue !== 'section') return;

    this.boundExpandCollapsedSection = (event) => {
      this.prepareSectionDropTargetUnderPointer(event);
    };
    document.addEventListener('dragover', this.boundExpandCollapsedSection);
  }

  stopCollapsedSectionDropTargeting() {
    if (this.boundExpandCollapsedSection) {
      document.removeEventListener('dragover', this.boundExpandCollapsedSection);
      this.boundExpandCollapsedSection = null;
    }

    document.querySelectorAll('.subtask-section-items.is-drop-target').forEach((el) => {
      el.classList.remove('is-drop-target');
    });
  }

  prepareSectionDropTargetUnderPointer(event) {
    const section = event.target?.closest?.('.subtask-section');
    const content = section?.querySelector(':scope > .subtask-section-items');

    document.querySelectorAll('.subtask-section-items.is-drop-target').forEach((el) => {
      if (el !== content) el.classList.remove('is-drop-target');
    });

    if (!content) return;

    if (content.classList.contains('d-none')) {
      content.classList.remove('d-none');

      const icon = section.querySelector('[data-turbo-collapse-target="icon"]');
      if (icon) {
        icon.classList.remove('fa-caret-right');
        icon.classList.add('fa-caret-down');
      }
    }

    const empty = !content.querySelector(':scope > [data-subtasks-sort-item]');
    content.classList.toggle('is-drop-target', empty);
  }

  // Badge counts are server-rendered; recompute from the live DOM after moves.
  syncSectionCounts() {
    if (this.scopeValue !== 'section') return;

    const listRoot = this.listRoot();
    if (!listRoot) return;

    listRoot.querySelectorAll('.subtask-section').forEach((section) => {
      const container = section.querySelector(':scope > .subtask-section-items');
      const badge = section.querySelector('[data-subtask-section-count]');
      if (!container || !badge) return;

      const count = container.querySelectorAll(':scope > [data-subtasks-sort-item]').length;
      badge.textContent = String(count);
    });
  }

  // Rails dom_id(task, :subtasks_list) => "subtasks_list_task_<id>"
  listRoot() {
    return this.element.closest('[id^="subtasks_list_"]') || this.element;
  }

  collapseNestedSubtasks(item) {
    const collapseContent = item.querySelector('[data-turbo-collapse-target="content"]');

    if (collapseContent && !collapseContent.classList.contains('d-none')) {
      const wasExpanded = true;
      collapseContent.classList.add('d-none');

      const icon = item.querySelector('[data-turbo-collapse-target="icon"]');
      if (icon) {
        icon.classList.remove('fa-caret-down');
        icon.classList.add('fa-caret-right');
      }

      return { wasExpanded, collapseContent, icon };
    }

    return null;
  }

  restoreExpandedState(item, state) {
    if (state && state.wasExpanded) {
      state.collapseContent.classList.remove('d-none');

      if (state.icon) {
        state.icon.classList.remove('fa-caret-right');
        state.icon.classList.add('fa-caret-down');
      }
    }
  }

  updatePosition(event) {
    const item = event.item;

    const sortableItem = item.classList.contains('sortable-item')
      ? item
      : item.querySelector('.sortable-item');

    if (!sortableItem) {
      console.error('Could not find sortable-item for position update');
      return;
    }

    const url = sortableItem.dataset.url;

    if (!url) {
      console.error('No URL found for position update');
      return;
    }

    // Position is global among the parent's top-level subtasks. When sorting
    // inside a section, event.newIndex is only section-local — derive the
    // index from DOM order across the whole subtasks list instead.
    const newPosition = this.globalPositionFor(sortableItem);
    const oldIndex = event.oldIndex;
    const payload = { position: newPosition };

    // Cross-section drops land in a .subtask-section-items container that
    // carries the destination section id (blank for Ungrouped).
    const sectionContainer = item.closest('.subtask-section-items');
    if (sectionContainer) {
      payload.section_id = sectionContainer.dataset.sectionId || null;
    }

    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).catch(error => {
      console.error('Error updating position:', error);
      this.revertPosition(item, oldIndex);
    });
  }

  // 1-based index among top-level sortable rows in the parent subtasks list
  // (works for both the flat list and within-section grouped lists).
  globalPositionFor(sortableItem) {
    const topLevel = Array.from(this.listRoot().querySelectorAll('.sortable-item.top-level-subtask'));
    const index = topLevel.indexOf(sortableItem);
    return index >= 0 ? index + 1 : 1;
  }

  revertPosition(item, oldIndex) {
    const parent = item.parentNode;
    const children = Array.from(parent.children);
    const currentIndex = children.indexOf(item);

    if (currentIndex !== oldIndex) {
      if (oldIndex === 0) {
        parent.insertBefore(item, parent.firstChild);
      } else if (oldIndex < children.length) {
        parent.insertBefore(item, children[oldIndex > currentIndex ? oldIndex + 1 : oldIndex]);
      } else {
        parent.appendChild(item);
      }
    }

    this.showErrorMessage("Failed to update position. The item has been moved back.");
  }

  showErrorMessage(message) {
    console.error(message);
  }
}
